/** Conversation with the robo-cat tutor (full «Разговор» screen and the narrow reader panel). */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTutor } from '../state/tutor.tsx';
import { routes, useRouter } from '../state/router.tsx';
import { getGeminiKey } from '../data/gemini.ts';
import { RoboCat } from './RoboCat.tsx';
import { Icon } from './Icon.tsx';
import './chat.css';

const SUGGESTIONS = ['Объясни грамматику урока', 'Ещё пример', 'Проверь меня'];

export function ChatPanel({ compact = false, subtitle }: { compact?: boolean; subtitle?: string }) {
  const { navigate } = useRouter();
  const { messages, pending, error, draft, setDraft, send } = useTutor();
  const [text, setText] = useState(draft);
  const listRef = useRef<HTMLDivElement>(null);
  const hasKey = getGeminiKey() !== null;

  // Take over a draft handed in from elsewhere («Спросить учителя»).
  useEffect(() => {
    if (draft) {
      setText(draft);
      setDraft('');
    }
  }, [draft, setDraft]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  const submit = (value: string) => {
    if (!value.trim() || pending) return;
    setText('');
    void send(value);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(text);
  };

  const catState = pending ? 'thinking' : text ? 'listening' : 'idle';

  return (
    <section className={`chat${compact ? ' chat--compact' : ''}`} aria-label="Разговор с учителем">
      <header className="chat__head">
        <RoboCat size={compact ? 46 : 62} state={catState} typedLength={text.length} followPointer={!compact} alive />
        <div>
          <h2 className={compact ? 't-section' : 't-card'}>ИИ-учитель</h2>
          <p className="t-meta">{subtitle ?? 'Отвечает по-русски, коротко и по уроку'}</p>
        </div>
      </header>

      <div className="chat__list" ref={listRef} aria-live="polite">
        {!hasKey && (
          <div className="chat__notice">
            <p>Чтобы учитель отвечал, добавьте свой бесплатный ключ Gemini.</p>
            <p className="t-meta">Ключ хранится только на этом устройстве — введите его один раз на каждом телефоне или компьютере.</p>
            <button type="button" className="btn btn--secondary" onClick={() => navigate(routes.profile)}>
              Открыть настройки ИИ
            </button>
          </div>
        )}
        {hasKey && messages.length === 0 && !pending && (
          <p className="empty">Спросите про слово, правило или предложение из урока.</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`bubble bubble--${m.role}`}>
            {m.role === 'tutor' && <span className="tutor-label">ИИ-учитель</span>}
            <p>{m.text}</p>
          </div>
        ))}
        {pending && (
          <div className="chat__thinking" aria-label="Учитель думает">
            <RoboCat size={40} state="thinking" />
          </div>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="chat__suggestions">
        {SUGGESTIONS.map((s) => (
          <button key={s} type="button" className="chip" disabled={pending || !hasKey} onClick={() => submit(s)}>
            {s}
          </button>
        ))}
      </div>
      <form className="chat__composer" onSubmit={onSubmit}>
        <label className="visually-hidden" htmlFor={compact ? 'chat-input-compact' : 'chat-input'}>
          Вопрос учителю
        </label>
        <textarea
          id={compact ? 'chat-input-compact' : 'chat-input'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit(text);
            }
          }}
          rows={compact ? 1 : 2}
          placeholder="Спросить про текст…"
        />
        <button type="submit" className="chat__send" disabled={!text.trim() || pending} aria-label="Отправить">
          <Icon name="send" size={20} />
        </button>
      </form>
    </section>
  );
}
